import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createTask,
  updateTask,
  deleteTask,
  completeTask,
  reopenTask,
} from '../services/tasksApi';
import { taskKeys } from './useTasks';
import type { CreateTaskRequest, UpdateTaskRequest } from '../types/task';

/**
 * Hook for task mutations (create, update, delete, complete, reopen)
 */
export const useTaskMutations = () => {
  const queryClient = useQueryClient();

  // Invalidate all task queries
  const invalidateTasks = () => {
    queryClient.invalidateQueries({ queryKey: taskKeys.all });
  };

  // Create task mutation
  const createTaskMutation = useMutation({
    mutationFn: (data: CreateTaskRequest) => createTask(data),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Update task mutation
  const updateTaskMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaskRequest }) =>
      updateTask(id, data),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: (id: string) => deleteTask(id),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Complete task mutation
  const completeTaskMutation = useMutation({
    mutationFn: (id: string) => completeTask(id),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  // Reopen task mutation
  const reopenTaskMutation = useMutation({
    mutationFn: (id: string) => reopenTask(id),
    onSuccess: () => {
      invalidateTasks();
    },
  });

  return {
    createTask: createTaskMutation,
    updateTask: updateTaskMutation,
    deleteTask: deleteTaskMutation,
    completeTask: completeTaskMutation,
    reopenTask: reopenTaskMutation,
    isLoading:
      createTaskMutation.isPending ||
      updateTaskMutation.isPending ||
      deleteTaskMutation.isPending ||
      completeTaskMutation.isPending ||
      reopenTaskMutation.isPending,
  };
};

export default useTaskMutations;
